import { Link } from 'react-router-dom';
import { SEOPageLayout } from '@/components/seo/SEOPageLayout';

const faq = [
  { q: 'How do I express love to someone I care about?', a: 'Be specific about what you love about them. Mention moments, qualities, or feelings. Authentic expression doesn\'t need to be poetic — it just needs to be true.' },
  { q: 'What if I\'m not good with words?', a: 'Say It NOW\'s AI companion JacKey helps you find the right words by asking gentle questions about your relationship. You just answer honestly, and it helps shape your message.' },
  { q: 'Can I send a message to a friend, not just a romantic partner?', a: 'Absolutely. Say It NOW supports messages for friends, mentors, siblings, colleagues — anyone who has touched your life.' },
  { q: 'Is Say It NOW free to use?', a: 'Yes. You can create and send your first messages completely free. Premium features like custom templates and voice recordings are available through credit bundles.' },
];

const related = [
  { label: 'Heartfelt Message to Mom', href: '/heartfelt-message-to-mom' },
  { label: 'Heartfelt Message to Dad', href: '/heartfelt-message-to-dad' },
  { label: 'Apology Message Examples', href: '/apology-message-examples' },
  { label: 'Thank You Message for Parents', href: '/thank-you-message-for-parents' },
];

export default function MessageToSomeoneYouLove() {
  return (
    <SEOPageLayout
      title="Message to Someone You Love — Find the Words"
      metaDescription="Find the right words for someone you love. Real examples for partners, friends, family, and mentors. Create a beautiful heartfelt message — free and private."
      h1="How to Write a Message to Someone You Love"
      subtitle="Love isn't always easy to say out loud. Here's how to write it down — beautifully."
      faq={faq}
      relatedPages={related}
    >
      <h2>Why We Struggle to Say "I Love You"</h2>
      <p>
        It should be the easiest thing in the world — telling someone they matter. But for most of us, it's one of the hardest. We worry about sounding awkward, being too vulnerable, or saying the "wrong" thing. So we say nothing. And nothing becomes a habit.
      </p>
      <p>
        The truth is: <strong>imperfect words spoken now are infinitely more valuable than perfect words never said.</strong> This page is here to help you break through that barrier — whether you're writing to a partner, a friend, a parent, or someone who changed your life.
      </p>

      <h2>Messages for Different Relationships</h2>

      <h3>For a Romantic Partner</h3>
      <blockquote>
        "I don't tell you this enough, but you make the ordinary feel extraordinary. Waking up next to you, laughing at nothing together, the way you know what I need before I do — I don't take any of it for granted. You are my favorite person."
      </blockquote>

      <h3>For a Best Friend</h3>
      <blockquote>
        "You've seen me at my worst and still chose to stay. That kind of loyalty isn't something I'll ever forget. I just want you to know — you're not just my friend, you're family. And I love you for that."
      </blockquote>

      <h3>For a Mentor or Teacher</h3>
      <blockquote>
        "You probably don't realize how much you changed my life. That one conversation we had — when you told me I was capable of more — I've carried it with me every single day since. Thank you for believing in me before I believed in myself."
      </blockquote>

      <h3>For a Sibling</h3>
      <blockquote>
        "We don't say this to each other, but I want you to know — growing up with you was the greatest gift. You made every hard thing easier just by being there. I love you, and I'm grateful we're in this life together."
      </blockquote>

      <h2>Tips for Writing an Authentic Love Message</h2>
      <ul>
        <li><strong>Be specific:</strong> "I love how you always know when I need a hug" beats "I love everything about you."</li>
        <li><strong>Be honest:</strong> Vulnerability is the ingredient that makes words unforgettable.</li>
        <li><strong>Be timely:</strong> Don't wait for a birthday or holiday. The best messages arrive unexpectedly.</li>
        <li><strong>Be yourself:</strong> Fancy language isn't required. Your real voice is what they want to hear.</li>
      </ul>

      <h2>Create a Message to Someone You Love</h2>
      <p>
        <Link to="/signup">Say It NOW</Link> is a free <strong>heartfelt message creator</strong> designed to help you express what matters most. Choose a beautiful template, write your message (or let AI help you), add photos, and share privately. Your words become a digital keepsake — a <em>message to a loved one</em> that lasts forever.
      </p>
    </SEOPageLayout>
  );
}
